import { KgsBankSelect } from '../ui/kgs-bank-select';
import { MAX_DEPOSIT_ORDER_CAPACITY, DEPOSIT_ORDER_CAPACITY_ERROR, isValidDepositOrderCapacity } from '../../lib/orderCapacity';
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { ordersAPI, traderAPI, OrderResponse, OrderUpdate } from '../../lib/api';
import { formatAmount } from '../../lib/utils';
import { balanceEvents, BALANCE_EVENTS } from '../../lib/events';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { BankAutocomplete } from '../ui/bank-autocomplete';
import { PhoneInput } from '../ui/phone-input';
import { CardInput } from '../ui/card-input';

interface EditOrderDialogProps {
    order: OrderResponse | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSaved: () => void;
}

interface EditOrderFormState {
    requisite_types: Array<'sbp' | 'card' | 'elqr'>;
    from_amount: string;
    min_amount: string;
    max_amount: string;
    phone_number: string;
    recipient_bank: string;
    recipient_name: string;
    recipient_card: string;
    qr_url: string;
    notes: string;
    is_paused: boolean;
}

const parseAmount = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const EditOrderDialog: React.FC<EditOrderDialogProps> = ({
    order,
    open,
    onOpenChange,
    onSaved,
}) => {
    const [form, setForm] = useState<EditOrderFormState>({
        requisite_types: [],
        from_amount: '',
        min_amount: '',
        max_amount: '',
        phone_number: '',
        recipient_bank: '',
        recipient_name: '',
        recipient_card: '',
        qr_url: '',
        notes: '',
        is_paused: false,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [exchangeRate, setExchangeRate] = useState<number | null>(null);
    const [loadingRate, setLoadingRate] = useState(false);

    useEffect(() => {
        if (!open) return;
        let active = true;
        setExchangeRate(null);
        setLoadingRate(true);
        traderAPI.getSettings().then(settings => {
            if (active) setExchangeRate(settings.exchange_rate ?? null);
        }).catch(() => {
            if (active) setExchangeRate(null);
        }).finally(() => {
            if (active) setLoadingRate(false);
        });
        return () => { active = false; };
    }, [open, order?.id]);

    useEffect(() => {
        if (!order || !open) return;

        setForm({
            requisite_types: order.requisite_types ?? [
                ...(order.phone_number ? ['sbp' as const] : []),
                ...(order.recipient_card ? ['card' as const] : []),
            ],
            from_amount: String(order.from_amount || ''),
            min_amount: String(order.min_amount || ''),
            max_amount: String(order.max_amount || ''),
            phone_number: order.phone_number || '',
            recipient_bank: order.recipient_bank || '',
            recipient_name: order.recipient_name || '',
            recipient_card: order.recipient_card || '',
            qr_url: order.qr_url || '',
            notes: order.notes || '',
            is_paused: Boolean(order.is_paused),
        });
        setError(null);
    }, [order, open]);

    const previewToAmount = useMemo(() => {
        const fromAmount = parseAmount(form.from_amount);
        if (!fromAmount || !exchangeRate) {
            return null;
        }
        return fromAmount / exchangeRate;
    }, [exchangeRate, form.from_amount]);

    const updateField = <K extends keyof EditOrderFormState>(
        field: K,
        value: EditOrderFormState[K]
    ) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        if (!order) return 'Ордер не найден';

        const fromAmount = parseAmount(form.from_amount);
        const reservedAmount = order.reserved_amount || 0;
        if (fromAmount <= 0) {
            return 'Введите сумму больше 0';
        }
        if (fromAmount < reservedAmount) {
            return `Сумма не может быть меньше зарезервированной: ${formatAmount(reservedAmount)} ${order.from_currency}`;
        }

        if (order.order_type === 'deposit') {
            if (order.capacity_policy_version === 1 && !isValidDepositOrderCapacity(fromAmount)) {
                return DEPOSIT_ORDER_CAPACITY_ERROR;
            }
            const minAmount = parseAmount(form.min_amount);
            const maxAmount = parseAmount(form.max_amount);
            if (minAmount <= 0 || maxAmount <= 0) {
                return 'Введите минимальную и максимальную сумму';
            }
            if (minAmount > maxAmount) {
                return 'Минимальная сумма не может быть больше максимальной';
            }
            if (maxAmount > fromAmount) {
                return 'Максимальная сумма не должна превышать общую сумму';
            }

            const phoneDigits = form.phone_number.replace(/\D/g, '');
            const cardDigits = form.recipient_card.replace(/\D/g, '');
            const hasPhone = order.from_currency === 'KGS'
                ? phoneDigits.length === 12 && phoneDigits.startsWith('996')
                : phoneDigits.length === 11 && phoneDigits.startsWith('7');
            const hasCard = cardDigits.length >= 13 && cardDigits.length <= 19;
            if (!form.requisite_types.length) {
                return 'Выберите хотя бы один тип реквизита';
            }
            if (form.requisite_types.includes('sbp') && !hasPhone) {
                return 'Для СБП укажите корректный номер телефона';
            }
            if (form.requisite_types.includes('card') && !hasCard) {
                return 'Для оплаты картой укажите корректный номер карты';
            }
            if (form.requisite_types.includes('elqr')) {
                if (!hasPhone || !form.recipient_name.trim() || !form.recipient_bank.trim()) {
                    return 'Для ElQR укажите имя, банк и номер телефона +996';
                }
                if (!form.qr_url.trim().toLowerCase().startsWith('https://')) {
                    return 'Для ElQR укажите HTTPS-ссылку для диплинка';
                }
            }
        }

        return null;
    };

    const handleSave = async () => {
        if (!order) return;

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        const fromAmount = parseAmount(form.from_amount);
        const payload: OrderUpdate = {
            is_paused: form.is_paused,
        };
        if (fromAmount !== order.from_amount) {
            payload.from_amount = fromAmount;
        }

        if (order.order_type === 'deposit') {
            payload.requisite_types = form.requisite_types;
            payload.min_amount = parseAmount(form.min_amount);
            payload.max_amount = parseAmount(form.max_amount);
            payload.phone_number = form.phone_number;
            payload.recipient_bank = form.recipient_bank;
            payload.recipient_name = form.recipient_name;
            payload.recipient_card = form.recipient_card;
            payload.qr_url = form.qr_url;
            payload.notes = form.notes;
        }

        try {
            setIsSaving(true);
            setError(null);
            await ordersAPI.updateOrder(order.id, payload);
            balanceEvents.emit(BALANCE_EVENTS.UPDATED);
            onSaved();
            onOpenChange(false);
        } catch (err: any) {
            setError(
                err.response?.data?.detail ||
                'Не удалось сохранить изменения'
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!order) return null;

    const isDeposit = order.order_type === 'deposit';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Редактировать реквизит</DialogTitle>
                    <DialogDescription>
                        {isDeposit
                            ? 'Измените лимит, диапазон платежей и банковские реквизиты'
                            : 'Измените сумму ордера или поставьте прием заявок на паузу'
                        }
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {error && (
                        <div className="p-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-between rounded-md border p-3">
                        <div>
                            <p className="font-medium">Прием заявок</p>
                            <p className="text-sm text-gray-600 dark:text-slate-300">
                                {form.is_paused
                                    ? 'Новые заявки не будут назначаться на этот реквизит'
                                    : 'Реквизит участвует в подборе для новых заявок'
                                }
                            </p>
                        </div>
                        <Switch
                            checked={!form.is_paused}
                            onCheckedChange={(checked) => updateField('is_paused', !checked)}
                            disabled={isSaving}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit_from_amount">
                                {isDeposit ? `Общая сумма ордера (${order.from_currency})` : `Сумма ${order.from_currency} к выдаче`}
                            </Label>
                            <Input
                                id="edit_from_amount"
                                type="number"
                                min="0"
                                step={order.order_type === 'deposit' && order.capacity_policy_version === 1 ? "1" : "0.01"}
                                max={order.order_type === 'deposit' && order.capacity_policy_version === 1 ? MAX_DEPOSIT_ORDER_CAPACITY : undefined}
                                value={form.from_amount}
                                onChange={(event) => updateField('from_amount', event.target.value)}
                                disabled={isSaving}
                            />
                            {(order.reserved_amount || 0) > 0 && (
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Зарезервировано: {formatAmount(order.reserved_amount || 0)} {order.from_currency}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="edit_to_amount">Примерный эквивалент в USDT (без комиссии)</Label>
                            <Input
                                id="edit_to_amount"
                                value={loadingRate ? 'Загрузка курса...' : previewToAmount === null ? 'Курс недоступен' : formatAmount(previewToAmount)}
                                disabled
                                className="bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-slate-200"
                            />
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                {exchangeRate
                                    ? `Курс: 1 USDT = ${formatAmount(exchangeRate)} ${order.from_currency}`
                                    : 'Оценка USDT недоступна'
                                }
                            </p>
                            <p className="text-xs text-gray-500 dark:text-slate-400">
                                Курс фиксируется отдельно при назначении каждой заявки.
                            </p>
                        </div>
                    </div>

                    {isDeposit && (
                        <>
                            <div className="space-y-2 border-t pt-4">
                                <p className="font-medium">Типы реквизитов</p>
                                <div className="flex flex-wrap gap-4">
                                    {(['card', 'sbp', ...(order.from_currency === 'KGS' ? ['elqr' as const] : [])] as const).map(type => (
                                        <label key={type} className="flex cursor-pointer items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={form.requisite_types.includes(type)}
                                                onChange={(event) => updateField('requisite_types', event.target.checked
                                                    ? [...form.requisite_types, type]
                                                    : form.requisite_types.filter(item => item !== type))}
                                                disabled={isSaving}
                                                className="h-4 w-4 rounded border-gray-300 dark:border-slate-700"
                                            />
                                            <span>{type === 'card' ? 'Банковская карта' : type === 'sbp' ? 'СБП' : 'ElQR'}</span>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-slate-400">Выберите способы оплаты для новых заявок и заполните соответствующие поля.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit_min_amount">Минимальная сумма ({order.from_currency})</Label>
                                    <Input
                                        id="edit_min_amount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.min_amount}
                                        onChange={(event) => updateField('min_amount', event.target.value)}
                                        disabled={isSaving}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="edit_max_amount">Максимальная сумма ({order.from_currency})</Label>
                                    <Input
                                        id="edit_max_amount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.max_amount}
                                        onChange={(event) => updateField('max_amount', event.target.value)}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
                                <PhoneInput
                                    id="edit_phone_number"
                                    label="Номер телефона"
                                    country={order.from_currency === 'KGS' ? 'KG' : 'RU'}
                                    value={form.phone_number}
                                    onChange={(value) => updateField('phone_number', value)}
                                    disabled={isSaving}
                                />

                                {order.from_currency === 'KGS' ? (
                                    <div className="space-y-2">
                                        <Label htmlFor="edit_recipient_bank">Банк получателя</Label>
                                        <KgsBankSelect
                                            id="edit_recipient_bank"
                                            value={form.recipient_bank}
                                            onChange={value => updateField('recipient_bank', value)}
                                            disabled={isSaving}
                                        />
                                    </div>
                                ) : (
                                    <BankAutocomplete
                                        id="edit_recipient_bank"
                                        label="Банк получателя"
                                        value={form.recipient_bank}
                                        onChange={(value) => updateField('recipient_bank', value)}
                                        placeholder="Начните вводить название банка..."
                                        disabled={isSaving}
                                    />
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="edit_recipient_name">
                                        {form.requisite_types.includes('elqr') ? 'Фамилия и имя получателя *' : 'Получатель'}
                                    </Label>
                                    <Input
                                        id="edit_recipient_name"
                                        type="text"
                                        value={form.recipient_name}
                                        onChange={(event) => updateField('recipient_name', event.target.value)}
                                        disabled={isSaving}
                                    />
                                </div>

                                <CardInput
                                    id="edit_recipient_card"
                                    label="Номер банковской карты"
                                    value={form.recipient_card}
                                    onChange={(value) => updateField('recipient_card', value)}
                                    disabled={isSaving}
                                />
                                {form.requisite_types.includes('elqr') && (
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="edit_qr_url">Ссылка для диплинка ElQR</Label>
                                        <Input
                                            id="edit_qr_url"
                                            type="url"
                                            value={form.qr_url}
                                            onChange={(event) => updateField('qr_url', event.target.value)}
                                            disabled={isSaving}
                                        />
                                        <p className="text-xs text-gray-500 dark:text-slate-400">QR-код будет создан автоматически из этой ссылки.</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="edit_notes">Примечание</Label>
                                <textarea
                                    id="edit_notes"
                                    value={form.notes}
                                    onChange={(event) => updateField('notes', event.target.value)}
                                    disabled={isSaving}
                                    className="w-full p-3 border border-gray-300 dark:border-slate-700 rounded-md resize-none h-20 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter className="mt-6">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                    >
                        Отмена
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="mr-2 h-4 w-4" />
                        )}
                        Сохранить
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
